import { CurrencyCode } from '@buggy/shared'
import axios from 'axios'
import dayjs from 'dayjs'
import { filter, isEmpty, isUndefined, toNumber } from 'lodash-es'
import { stringifyUrl } from 'query-string'
import { useCallback } from 'react'
import { useTheme } from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import { useLazyFetchKolContentQuery } from '@/api/workspace-api/kol-api'
import { KOLAnalyze } from '@/api/workspaces-api'
import kolrFallbackThumbnailSrc from '@/assets/images/kolr-fallback-thumbnail.png'
import getLevel from '@/components/common/k-score/get-level'
import { ExportSetting } from '@/components/kol/export/kol-export-modal'
import routes from '@/hooks/use-authorization/routes'
import useBrandInfo from '@/hooks/use-brand-info'
import useCustomizedTag from '@/hooks/use-customized-tag'
import { useI18n } from '@/hooks/use-i18n'
import { useShouldDisplayQuotations } from '@/hooks/use-should-display-quotations'
import { useIntl } from '@/i18n/hooks/use-intl'
import { LinkAnalysis } from '@/tests/mock-data/mock-content-distribution'
import { FetchKolAnalyzeResponse, RelatedKol } from '@/types/api/kols'
import { type Nullable } from '@/types/nullable'
import { Post } from '@/types/redux/kol-content'
import { ListResource } from '@/types/resource-type'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import type {
  KpiSchema,
  PerformanceQuartileMetrics,
} from '@/types/schema/kpi-schema'
import checkValidImageResponse from '@/utils/check-valid-image-response'
import { Platform, PlatformCode } from '@/utils/convert-platform'
import { convertToPercentage } from '@/utils/convert-to-percentage'
import getKScoreLevelI18nId from '@/utils/detail/get-score-level-i18n-id'
import { Potential } from '@/utils/detail/potential'
import env from '@/utils/env'
import Slide, {
  KpiItem,
  PostItem,
  TopPostType,
} from '@/utils/export-pptx/types/slide'
import { getThumbnail } from '@/utils/get-thumbnails'
import { useGender } from '@/utils/hooks/use-gender'
import evaluateMetricPR from '@/utils/kol/evaluate-metric-pr'
import logger from '@/utils/logger'
import { getContentImageUrlFromApiServer } from '@/utils/post/get-image-url-from-api-server'
import safeDivide from '@/utils/safe-divide'
import sleep from '@/utils/sleep'

interface GenerateKolSlideProps {
  workspaceId: number
  currencyCode?: CurrencyCode
  platform: Platform
  period: ExportSetting['period']
  kolInfo: KolSchemaModel
  kpi: KpiSchema
  kolAnalyze: FetchKolAnalyzeResponse['data']
  linkAnalyze?: LinkAnalysis[]
  mentionedBrands?: ListResource<RelatedKol>
  postQuotationRange?: string
  videoQuotationRange?: string
}

type GenerateKolSlide = (props: GenerateKolSlideProps) => Promise<Slide>

type UseGenerateKolSlide = () => {
  generateKolSlide: GenerateKolSlide
}

const axiosInstance = axios.create()

// 設置請求攔截器來檢查 URL 是否在白名單中
axiosInstance.interceptors.request.use((config) => {
  const whitelist = [
    'https://thumbnail.kolradar.com', // todo: 等確認 api 的貼圖來源也改 *.kolr.ai 後移除
    'https://thumbnail.kolr.ai',
    env.PUBLIC_K6RSATELLITE_API_BASE_URL,
  ]

  const url = config.url ?? ''
  if (!whitelist.some((whitelistedUrl) => url.startsWith(whitelistedUrl))) {
    return Promise.reject(new Error('URL not in whitelist'))
  }

  return config
})

const useGenerateKolSlide: UseGenerateKolSlide = () => {
  const { formatMessage, formatDate, formatNumber } = useIntl()
  const { brandLogoSrc } = useBrandInfo()
  const theme = useTheme()
  const { getGenderText } = useGender()
  const { locale, language } = useI18n()

  const { getCustomizedTagLocalizedName } = useCustomizedTag()
  const [fetchKolContent] = useLazyFetchKolContentQuery()
  const shouldDisplayQuotations = useShouldDisplayQuotations()

  const getKolThumbnail = useCallback(
    async (kolId: string): Promise<string> => {
      try {
        const url = getThumbnail(kolId)
        const response = await fetch(url)

        checkValidImageResponse(response)

        return url
      } catch (error) {
        return kolrFallbackThumbnailSrc
      }
    },
    [],
  )

  const getPostThumbnailPost = useCallback(
    async (kolId: string, post: Post): Promise<string> => {
      const RETRY_COUNT = 5

      const postUrls = [post.thumbnail, getContentImageUrlFromApiServer(post)]

      const redundantUrl = getThumbnail(kolId)

      // 先嘗試 postUrls 中的所有 URL，最多嘗試 RETRY_COUNT 次
      for (let attempt = 0; attempt < RETRY_COUNT; attempt++) {
        for (const url of postUrls) {
          try {
            const response = await axiosInstance.get(url)

            checkValidImageResponse(response)

            return url // 成功則立即返回
          } catch (error) {
            sleep(250)
          }
        }
      }

      // 如果所有 postUrls 都失敗，嘗試 redundantUrl 一次
      try {
        const response = await axiosInstance.get(redundantUrl)
        checkValidImageResponse(response)
        return redundantUrl // 成功則立即返回
      } catch (error) {
        return kolrFallbackThumbnailSrc
      }
    },
    [],
  )

  const generateKolSlide: GenerateKolSlide = useCallback(
    async ({
      workspaceId,
      currencyCode,
      platform,
      period,
      kolInfo,
      kpi,
      kolAnalyze,
      linkAnalyze,
      mentionedBrands,
      postQuotationRange,
      videoQuotationRange,
    }) => {
      const isInstagram = platform.code === PlatformCode.Instagram
      const isTikTok = platform.code === PlatformCode.TikTok
      const isThreeMonths = period === 'three_months'
      const isMajorPlatform = [
        PlatformCode.Instagram,
        PlatformCode.Facebook,
        PlatformCode.YouTube,
      ].includes(platform.code)

      const link = encodeURIComponent(
        stringifyUrl({
          url: `${env.LANDING_URL}${routes.publicKolDetail.stringifyUrl(
            {
              kolId: kolInfo.id,
            },
            {
              platform: platform.shortcode,
              lang: language.i18nCode,
              from: 'KD pptx',
            },
          )}
          `,
        }),
      )

      const isHighPotential = isMajorPlatform
        ? kpi.potential === Potential.High
        : false

      const followersItem: KpiItem = {
        title: formatMessage({ id: 'campaign:followers_text' }),
        primaryText: formatNumber(kpi.follower),
      }
      const fansUpRateItem = ((): KpiItem => {
        const fansUpRate = isThreeMonths ? kpi.fansUpRate3m : kpi.fansUpRate6m
        const fansUpRatePerformance = isThreeMonths
          ? kpi.fansUpRatePerformance3m
          : kpi.fansUpRatePerformance6m

        const fansUpRatePr = evaluateMetricPR(fansUpRate, fansUpRatePerformance)

        return {
          title: formatMessage({ id: 'kol:platform_data_fans_up_rate' }),
          primaryText: formatNumber(fansUpRate, { isPercentageDisplay: true }),
          secondaryTextData: [
            {
              title: !isUndefined(fansUpRatePr)
                ? formatMessage({ id: fansUpRatePr.rating })
                : '',
              color: fansUpRatePr?.color,
            },
          ],
        }
      })()
      const interactiveRateItem = ((): KpiItem => {
        const interactiveRate = isThreeMonths
          ? kpi.interactiveRate3m
          : kpi.interactiveRate6m

        const interactiveRatePerformance = isThreeMonths
          ? kpi.interactiveRatePerformance3m
          : kpi.interactiveRatePerformance6m

        const interactiveRatePr = evaluateMetricPR(
          interactiveRate,
          interactiveRatePerformance,
        )

        return {
          title: formatMessage({ id: 'kol:platform_data_avg_eng_rate' }),
          primaryText: formatNumber(interactiveRate, {
            isPercentageDisplay: true,
          }),
          secondaryTextData: [
            {
              title: !isUndefined(interactiveRatePr)
                ? formatMessage({ id: interactiveRatePr.rating })
                : '',
              color: interactiveRatePr?.color,
            },
          ],
        }
      })()
      const viewRateItem = ((): KpiItem => {
        const viewRate = ((): number | null => {
          if (isTikTok || isInstagram) {
            return isThreeMonths
              ? kpi.avgShortPlayRate3m
              : kpi.avgShortPlayRate6m
          }

          return isThreeMonths ? kpi.avgPvRate3m : kpi.avgPvRate6m
        })()

        const viewRatePerformance =
          ((): Nullable<PerformanceQuartileMetrics> => {
            if (isInstagram) {
              return isThreeMonths
                ? kpi?.avgShortViewRatePerformance3m
                : kpi?.avgShortViewRatePerformance6m
            }

            return isThreeMonths
              ? kpi?.avgPvRatePerformance3m
              : kpi?.avgPvRatePerformance6m
          })()

        const viewRatePr = evaluateMetricPR(viewRate, viewRatePerformance)

        return {
          title: formatMessage({
            id: isInstagram
              ? 'kol:reels_average_play_rate'
              : 'kol:card_view_rate',
          }),
          primaryText: viewRate
            ? formatNumber(viewRate, { isPercentageDisplay: true })
            : 'N/A',
          secondaryTextData: [
            {
              title:
                viewRate && !isUndefined(viewRatePr)
                  ? formatMessage({ id: viewRatePr.rating })
                  : '',
              color: viewRatePr?.color,
            },
          ],
        }
      })()

      const audienceGenderItem: KpiItem = ((): KpiItem => {
        if (!kpi.audience?.mainGender) {
          return {
            title: formatMessage({ id: 'kol:audience_main_gender' }),
            primaryText: 'N/A',
          }
        }

        return {
          title: formatMessage({ id: 'kol:audience_main_gender' }),
          primaryText: formatMessage({
            id:
              kpi.audience.mainGender.code === 'male'
                ? 'kol:edit_field_gender_male'
                : 'kol:edit_field_gender_female',
          }),
          secondaryTextData: [
            {
              title: `${convertToPercentage(
                kpi.audience.mainGender.weight * 100,
                0,
              )}%`,
            },
          ],
        }
      })()
      const audienceAgeItem: KpiItem = ((): KpiItem => {
        if (!kpi.audience?.mainAge) {
          return {
            title: formatMessage({ id: 'kol:audience_main_age' }),
            primaryText: 'N/A',
          }
        }

        return {
          title: formatMessage({ id: 'kol:audience_main_age' }),
          primaryText: kpi.audience.mainAge.code,

          secondaryTextData: [
            {
              title: formatMessage({
                id: 'general:age',
              }),
              color: theme.colors.text.primary,
            },
            {
              title: formatNumber(kpi.audience.mainAge.weight, {
                isPercentageDisplay: true,
              }),
            },
          ],
        }
      })()
      const postAdvertorialRate = ((): KpiItem => {
        const analyze = filter(kolAnalyze, (analyzes, key) => {
          if (!analyzes.some((analyze) => analyze.isAdvertorial)) {
            return false
          }

          return key === platform.code
        })
          .flat()
          .filter((kolAnalyze) => {
            const diff = dayjs().diff(kolAnalyze.publishedAt, 'months', true)
            return diff <= (isThreeMonths ? 3 : 6)
          })
        const advertorialAnalyze = filter(
          analyze,
          nameof<KOLAnalyze>((p) => p.isAdvertorial),
        )

        return {
          title: formatMessage({
            id: 'kol:export_post_advertorial_rate',
          }),
          primaryText: formatNumber(
            safeDivide(advertorialAnalyze.length, analyze.length),
            { isPercentageDisplay: true },
          ),
        }
      })()
      const kScoreItem = ((): KpiItem => {
        const score = isThreeMonths ? kpi.score3m : kpi.score6m

        if (!score) {
          return {
            title: 'K-Score',
            primaryText: 'N/A',
          }
        }

        const level = getLevel(score)

        return {
          title: 'K-Score',
          primaryText: score.toString(),
          secondaryTextData: [
            {
              title: formatMessage({
                id: 'general:score',
              }),
              color: theme.colors.text.primary,
            },
            {
              title: formatMessage({
                id: getKScoreLevelI18nId(level.type),
              }),
              color: level.scoreColor,
            },
          ],
        }
      })()
      const postCountItem: KpiItem = {
        title: formatMessage({ id: 'kol:card_content' }),
        primaryText: formatNumber(
          isThreeMonths ? kpi.postCount3m : kpi.postCount6m,
        ),
      }
      const avgInteractiveCountItem: KpiItem = {
        title: formatMessage({ id: 'kol:data_from_kol_avg_eng' }),
        primaryText: formatNumber(
          isThreeMonths ? kpi.interactive3m : kpi.interactive6m,
        ),
      }
      const avgViewCountItem = ((): KpiItem => {
        const avgPv = ((): number | null => {
          if (isTikTok || isInstagram) {
            return isThreeMonths
              ? kpi.avgShortPlayCount3m
              : kpi.avgShortPlayCount6m
          }

          return isThreeMonths ? kpi.avgPv3m : kpi.avgPv6m
        })()

        return {
          title: formatMessage({ id: 'kol:card_avg_view' }),
          primaryText: avgPv ? formatNumber(avgPv) : 'N/A',
        }
      })()

      const topPostTypes = ((): TopPostType[] | undefined => {
        const linkAnalysis = linkAnalyze?.find(
          (analysis) => toNumber(analysis.platformKey) === platform.type,
        )

        const platformDatum = linkAnalysis?.[
          isThreeMonths ? '3m' : '6m'
        ]?.slice(0, 5)

        if (!isMajorPlatform || !platformDatum) {
          return undefined
        }

        return platformDatum.map((platformData): TopPostType => {
          return {
            title: getCustomizedTagLocalizedName(
              platformData.customizedTagName,
            ),
            value: formatNumber(platformData.customizedTagPostCountRatio, {
              isPercentageDisplay: true,
            }),
          }
        })
      })()

      const fetchKolContentResponse = await fetchKolContent({
        workspaceId,
        radar_uuid: kolInfo.id,
        dc_id_hashed: kolInfo.dcIdHashed,
        platform: platform.shortcode,
        sort: 'engagement',
        size: 3,
      })
        .unwrap()
        .catch((error) => {
          logger.error(error)

          return undefined
        })

      const recentlyMentionedBrands =
        isInstagram && mentionedBrands?.data && mentionedBrands.data.length > 0
          ? mentionedBrands.data
              .map((brand) => brand.name)
              .slice(0, 3)
              .join(' 、 ')
          : undefined

      const posts = fetchKolContentResponse
        ? await Promise.all(
            fetchKolContentResponse.data.map(
              async (post): Promise<PostItem> => {
                const shareCount = [
                  PlatformCode.Facebook,
                  PlatformCode.Twitter,
                ].includes(platform.code)
                  ? formatNumber(post.shareCount)
                  : undefined
                const viewCount =
                  [PlatformCode.YouTube, PlatformCode.TikTok].includes(
                    platform.code,
                  ) ||
                  post.isVideo ||
                  post.viewCount > 0
                    ? formatNumber(post.viewCount)
                    : undefined
                const playCount =
                  platform.code === PlatformCode.Instagram && post.isShort
                    ? formatNumber(post.playCount ?? 0)
                    : undefined

                const thumbnailUrl = await getPostThumbnailPost(
                  kolInfo.id,
                  post,
                )

                return {
                  thumbnail: thumbnailUrl,
                  title: post.title.trim() + post.text.trim(),
                  link: post.url,
                  likeCount: formatNumber(post.likeCount),
                  commentCount: formatNumber(post.commentCount),
                  playCount,
                  shareCount,
                  viewCount,
                }
              },
            ),
          )
        : undefined

      return {
        type: 'kd',
        kolInfo: {
          platform: platform.shortcode,
          thumbnail: await getKolThumbnail(kolInfo.id),
          link,
          name: kolInfo.name,
          isHighPotential,
          alias: kolInfo.subName.join(' | '),
          gender: getGenderText(kolInfo.gender),
          property: kolInfo.customizedTagsNames
            .map(getCustomizedTagLocalizedName)
            .join(' · '),
          recentlyMentionedBrands: recentlyMentionedBrands,
          postQuotationRange: shouldDisplayQuotations(platform.shortcode)
            ? postQuotationRange
            : undefined,
          videoQuotationRange: shouldDisplayQuotations(platform.shortcode)
            ? videoQuotationRange
            : undefined,
        },
        kpi: isMajorPlatform
          ? [
              followersItem,
              fansUpRateItem,
              interactiveRateItem,
              viewRateItem,
              audienceGenderItem,
              audienceAgeItem,
              postAdvertorialRate,
              kScoreItem,
            ]
          : [
              followersItem,
              fansUpRateItem,
              interactiveRateItem,
              viewRateItem,
              postCountItem,
              avgInteractiveCountItem,
              avgViewCountItem,
            ],
        topPostTypes: isEmpty(topPostTypes) ? undefined : topPostTypes,
        posts,
        logoSrc: brandLogoSrc,
        updateAt: formatDate(kpi.updateTime),
        currencyCode,
        period,
        locale,
      }
    },
    [
      language.i18nCode,
      formatMessage,
      formatNumber,
      fetchKolContent,
      getKolThumbnail,
      getGenderText,
      getCustomizedTagLocalizedName,
      brandLogoSrc,
      formatDate,
      locale,
      theme.colors.text.primary,
      getPostThumbnailPost,
    ],
  )

  return {
    generateKolSlide,
  }
}

export default useGenerateKolSlide
